<script setup lang="ts">
import 'shepherd.js/dist/css/shepherd.css';
import '@assets/css/style.css';

import { useUrqlStore } from '@stores/useUrqlStore';
import { useI18n } from 'vue-i18n';
import { onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { getMessaging, onMessage } from 'firebase/messaging';
import { useAuthStore } from '@stores/useAuthStore';
import { useNotificationStore } from '@stores/useNotificationStore';
import { isIosOrAndroid } from '@helpers/mobile';
import { fetchCmsLanguages } from '@core-queries/cmsLanguages';
import { fetchCmsPackage } from '@core-queries/cmsPackage';
import { mergeContentPages, createLocaleMessage } from '@core-utils/cms';

import type { CmsLanguage, CmsPage } from '@core-types/cms';

const { gqlClient } = toRefs(useUrqlStore());
const { setLocaleMessage } = useI18n();

const pagesRef = ref<CmsPage[]>([]);
const languagesRef = ref<CmsLanguage[]>([]);

const router = useRouter();
const { setNotificationRoute, checkForFcmToken } = useNotificationStore();
const { authenticated } = toRefs(useAuthStore());

if (isIosOrAndroid()) {
  const messaging = getMessaging(useFirebaseApp());

  const stopListeningEventHandler = onMessage(messaging, payload => {
    if (payload.notification) {
      const { title = 'Mijn Mogelijk', body } = payload.notification;
      const notification = new Notification(title, { body });

      notification.addEventListener('click', async () => {
        let fullRoute = '/';

        if (payload.data?.action === 'tabs.investor.claimsAndOptions' && payload.data?.propertyId) {
          fullRoute = `/claims-en-opties?id=${payload.data.propertyId}`;
        } else if (payload.data?.action === 'tabs.investor.investments') {
          fullRoute = '/investeringen';
        }

        setNotificationRoute(fullRoute);
        router.push(fullRoute);
        notification.close();
      });
    }
  });

  onUnmounted(stopListeningEventHandler);
}

const initiateLanguages = async () => {
  const cmsPackageIds = ((import.meta.env.VITE_CMS_PACKAGE_ID || '') as string)
    .split(',')
    .map(id => id.trim()
  )
  const promises = [
    fetchCmsLanguages(gqlClient.value),
    ...cmsPackageIds.map(id => fetchCmsPackage(gqlClient.value, { id: Number(id) })),
  ] as const;
  const [languages, investorsContent, componentContent] = await Promise.all(promises);

  languagesRef.value = languages.cmsLanguages;

  const contentPages = mergeContentPages(investorsContent.cmsPackage.pages, componentContent.cmsPackage.pages);
  pagesRef.value = contentPages;

  for (const language of languagesRef.value) {
    const content = createLocaleMessage(pagesRef.value, language.id);
    setLocaleMessage(language.code, content);
  }
};

onMounted(async () => {
  await initiateLanguages();

  if (isIosOrAndroid() && authenticated.value && 'Notification' in window) {
    await Notification.requestPermission();
    checkForFcmToken();
  }
});
</script>

<template>
  <NuxtPage />
</template>
